import { useEffect, useRef, useState } from 'react';
import { getCompanyPorImovel } from '../services/selectors';
import { formatSelects } from './FunctionsGlobals';

/**
 * INC-0089 (cs604 Place Imóveis, 22/09/2026) — select de Condomínio "eliminatório" na busca da
 * home: escolhido o tipo / a cidade / o bairro, a lista de condomínios passa a mostrar só os que
 * têm imóvel publicado naquele recorte (ex.: "Casa em Condomínio" lista o Horizon Clube e deixa
 * o Loreto, que só tem apartamento, de fora). Sem nenhum desses filtros a lista é a completa.
 *
 * GATE por codsite (regra da casa: pedido de um cliente em código dos ~300 sites sai restrito à
 * licença que pediu). Liberar para todos = `new Set(['*'])`.
 */
const CODSITES_CONDOMINIO_DEPENDENTE: ReadonlySet<number | '*'> = new Set<number | '*'>([604]);

export function condominioDependenteDe(site: any, codsite?: any): boolean {
    if (CODSITES_CONDOMINIO_DEPENDENTE.has('*')) return true;
    const id = Number(site?.id ?? codsite);
    return Number.isInteger(id) && id > 0 && CODSITES_CONDOMINIO_DEPENDENTE.has(id);
}

export type OpcaoSelect = { label: string; value: any };

// {label,value} | [{label,value}] | null → só os ids válidos
const soIds = (lista: any): number[] =>
    (Array.isArray(lista) ? lista : lista ? [lista] : [])
        .map((o: any) => Number(o?.value ?? o))
        .filter((n: number) => Number.isInteger(n) && n > 0);

export function useCondominiosDependentes(p: {
    ativo: boolean;
    /** lista completa (allCompany) — é o que sai quando não há filtro ou a consulta falha */
    todos: OpcaoSelect[];
    tipos: any;
    cidade: any;
    bairros: any;
    origin?: string;
    codsite?: number;
    selecionado: OpcaoSelect | null | undefined;
    onLimparSelecionado: () => void;
}): { opcoes: OpcaoSelect[]; carregando: boolean; filtrado: boolean } {
    const tipos = soIds(p.tipos);
    const bairros = soIds(p.bairros);
    const cidade = soIds(p.cidade)[0] ?? 0;
    const filtrado = p.ativo && (tipos.length > 0 || cidade > 0 || bairros.length > 0);
    // chave estável do filtro: o effect só roda quando o recorte muda de verdade
    const chave = filtrado ? JSON.stringify({ tipos, cidade, bairros }) : '';

    const [lista, setLista] = useState<OpcaoSelect[] | null>(null);
    const [carregando, setCarregando] = useState(false);
    const seq = useRef(0);
    const limparRef = useRef(p.onLimparSelecionado);
    limparRef.current = p.onLimparSelecionado;

    useEffect(() => {
        const minha = ++seq.current;
        if (!chave) {
            setLista(null);
            setCarregando(false);
            return;
        }
        setCarregando(true);
        const timer = setTimeout(async () => {
            const { tipos: t, cidade: c, bairros: b } = JSON.parse(chave);
            const data = await getCompanyPorImovel(p.origin, p.codsite, { tipos: t, cidade: c, bairros: b });
            if (minha !== seq.current) return; // resposta de um filtro que já mudou
            // erro/resposta inesperada → null → volta à lista completa (nunca some com o campo)
            setLista(data ? formatSelects(data.empreendimentos) : null);
            setCarregando(false);
        }, 250);
        return () => clearTimeout(timer);
    }, [chave, p.origin, p.codsite]);

    // O condomínio já escolhido saiu da lista filtrada → limpa, senão a busca levaria um
    // condomínio incoerente com o tipo / cidade / bairro.
    const selecionadoId = p.selecionado ? String(p.selecionado.value) : '';
    useEffect(() => {
        if (!lista || !selecionadoId) return;
        if (!lista.some((o) => String(o.value) === selecionadoId)) limparRef.current();
    }, [lista, selecionadoId]);

    return { opcoes: lista ?? p.todos, carregando, filtrado };
}
